import React, { useState } from 'react';
import Person from '@mui/icons-material/Person';
import { 
  Container, 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Link as MuiLink,
  Divider,
  InputAdornment,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff,
  Email,
  Lock
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Auth = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    role: 'customer',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setAuthError('');
    setErrors({});
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'rememberMe' ? checked : value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Additional validations for registration
    if (tabValue === 1) {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setAuthError('');
      
      // In a real app, this would be an API call to the backend
      setTimeout(() => {
        setIsSubmitting(false);
        
        if (tabValue === 0) {
          // Login logic
          console.log('Logging in with:', formData.email);
          
          // Simulate successful login
          localStorage.setItem('authToken', 'sample-token');
          localStorage.setItem('userRole', formData.role); // For demo purposes
          
          // Redirect based on role
          if (formData.role === 'admin') {
            navigate('/admin/dashboard');
          } else if (formData.role === 'store_owner') {
            navigate('/store-owner/dashboard');
          } else {
            navigate('/customer/dashboard');
          }
        } else {
          // Registration logic
          console.log('Registering with:', formData);
          
          // Simulate successful registration
          setTabValue(0); // Switch to login tab
          setAuthError('');
          setFormData({
            ...formData,
            password: '',
            confirmPassword: ''
          });
          // Show success message
          alert('Registration successful! Please log in with your credentials.');
        }
      }, 1500);
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ my: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth" 
            sx={{ mb: 4 }}
          >
            <Tab label="Login" />
            <Tab label="Register" />
          </Tabs>
          
          {authError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {authError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            {tabValue === 1 && (
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete={tabValue === 0 ? 'current-password' : 'new-password'}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            {tabValue === 1 && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  )
                }}
              />
            )}
            
            {tabValue === 1 && (
              <FormControl component="fieldset" sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Register as:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.role === 'customer'}
                          onChange={() => setFormData({...formData, role: 'customer'})}
                          name="roleCustomer"
                        />
                      }
                      label="Customer"
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.role === 'store_owner'}
                          onChange={() => setFormData({...formData, role: 'store_owner'})}
                          name="roleStoreOwner"
                        />
                      }
                      label="Store Owner"
                    />
                  </Grid>
                </Grid>
              </FormControl>
            )}
            
            {tabValue === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Remember me"
                />
                <MuiLink component={Link} to="/forgot-password" variant="body2">
                  Forgot password?
                </MuiLink>
              </Box>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 
                (tabValue === 0 ? 'Logging in...' : 'Registering...') : 
                (tabValue === 0 ? 'Login' : 'Register')}
            </Button>
            
            {/* For demo purposes only - quick login buttons */}
            {tabValue === 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" align="center" gutterBottom>
                  Demo Quick Login:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          email: 'admin@example.com',
                          password: 'password',
                          role: 'admin'
                        });
                      }}
                    >
                      Admin
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          email: 'store@example.com',
                          password: 'password',
                          role: 'store_owner'
                        });
                      }}
                    >
                      Store Owner
                    </Button>
                  </Grid>
                  <Grid item xs={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          email: 'customer@example.com',
                          password: 'password',
                          role: 'customer'
                        });
                      }}
                    >
                      Customer
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              {tabValue === 0 ? "Don't have an account? " : "Already have an account? "}
              <MuiLink 
                component="button" 
                variant="body2"
                onClick={() => setTabValue(tabValue === 0 ? 1 : 0)}
              >
                {tabValue === 0 ? "Register" : "Login"}
              </MuiLink>
            </Typography>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default Auth;
